import type { Ref } from "react";
import React, { forwardRef, memo, useCallback, useState } from "react";
import clsx from "clsx";
import { useUniqueId } from "../../../../../../common-deprecated/hooks";
import type { IconVariantTypes } from "../atoms/Icon.types";
import Icon from "../atoms/Icon";
import styles from "./TextInput.module.scss";

type Props = {
    className?: string;
    label: string;
    hideLabel?: boolean;
    variant?: "default" | "search";
    invalid?: boolean;
    showRequired?: boolean;
    prefixIcon?: IconVariantTypes;
    suffixIcon?: IconVariantTypes;
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

const TextInput = forwardRef(
    (
        {
            label,
            variant = "default",
            invalid,
            showRequired,
            hideLabel,
            prefixIcon,
            suffixIcon,
            className,
            onChange,
            ...rest
        }: Props,
        ref: Ref<HTMLInputElement>,
    ) => {
        const [isFilled, setIsFilled] = useState<boolean>(false);
        const uniqueId = useUniqueId();

        const handleChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                onChange?.(event);
                setIsFilled(event.target.value.trim().length > 0);
            },
            [onChange],
        );

        return (
            <div className={clsx(styles[variant], invalid && styles.invalid, isFilled && styles.filled, className)}>
                <label
                    htmlFor={uniqueId}
                    className={clsx(hideLabel && styles["sr-only"], showRequired && styles.required)}
                >
                    {label}
                </label>
                <div className={styles["input-wrapper"]}>
                    {prefixIcon && <Icon variant={prefixIcon} />}
                    <input
                        id={uniqueId}
                        onChange={handleChange}
                        name={label}
                        className={styles.input}
                        {...rest}
                        ref={ref}
                    />
                    {suffixIcon && <Icon variant={suffixIcon} />}
                </div>
            </div>
        );
    },
);

export default memo(TextInput);
